/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useRef, useCallback } from 'react';

import AtomicSDK, {
  AACStreamContainer,
  HorizontalContainerConfig,
} from '@atomic.io/action-cards-web-sdk';
import { useAtomic } from './AtomicProvider';
import { Box, styled } from '@mui/material';
import _ from 'lodash';

const AtomicStyleWrapper = styled(Box)`
  margin-left: -10px;
  display: grid;
  .atomic-sdk-frame {
    width: 100%;
  }
`;

interface AtomicEmbedProps {
  containerId: string;
  containerConfig?: Partial<HorizontalContainerConfig>;
  onChange?: (cards: { cardCount: number; totalCards: number }) => void;
}

const AtomicEmbedHorizontal: React.FC<AtomicEmbedProps> = ({
  containerId,
  containerConfig,
  onChange,
}) => {
  const { atomicReady, onLinkButtonPressed } = useAtomic();

  // Create a reference to the embedded stream container
  const embedRef = useRef<AACStreamContainer>();
  const [cardCount, setCardCount] = React.useState(0);

  const horizontalEmbed = useCallback(
    (el: HTMLDivElement) => {
      if (!atomicReady) {
        return;
      }
      if (embedRef.current) {
        // Clean up any existing instance we might have
        embedRef.current.stop();
      }
      if (el && atomicReady) {
        el.innerHTML = '';

        embedRef.current = AtomicSDK.embed(
          el,
          {
            streamContainerId: containerId,
            enabledUiElements: {
              cardListHeader: false,
              cardListToast: false,
              launcherButton: {
                disabled: true,
              },
            },
            features: {
              horizontalContainerConfig: _.merge(
                {
                  enabled: true,
                  cardWidth: 354,
                  emptyStyle: 'standard',
                  headerAlignment: 'center',
                  scrollMode: 'snap',
                  lastCardAlignment: 'left',
                },
                containerConfig,
              ),
            },
            onCardCountChanged: (cardCount, totalCards) => {
              onChange?.({ cardCount, totalCards });
              setCardCount(totalCards);
            },
            onSubmitButtonPressed(payload) {
              onLinkButtonPressed(payload);
            },
            onLinkButtonPressed(payload) {
              onLinkButtonPressed(payload);
            },
          },
          true,
        );
      }
    },
    [atomicReady, containerConfig],
  );

  if (!atomicReady) {
    return null;
  }

  const display = cardCount === 0 ? { display: 'none' } : {};

  return (
    <AtomicStyleWrapper sx={display} ref={horizontalEmbed} className='atomic-embed-horizontal' />
  );
};

export default AtomicEmbedHorizontal;
